import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ContactNote,
    CreateContactNoteRequest,
    UpdateContactNoteRequest,
} from '../../types/ContactNote';
import { sortNotesByDate, sortNotesByPinned } from '../../utils/contactNotesHelper';
import { hasDistinctNoteId } from '../../utils/functionalFilters';
import {
    requestContactNotes,
    requestCreateContactNote,
    requestDeleteContactNote,
    requestEditContactNote,
} from '../notes/notes';

export function useNotes(contactId: number) {
    return useQuery<ContactNote[], Error>({
        queryKey: [`contact-${contactId}`, 'notes'],
        queryFn: async ({ signal }) =>
            await requestContactNotes(contactId, signal).then(res =>
                res.sort(sortNotesByDate).sort(sortNotesByPinned),
            ),
    });
}

export function useDeleteNote(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (noteId: number) => requestDeleteContactNote(contactId, noteId),
        onMutate: async (noteId: number) => {
            await queryClient.cancelQueries({ queryKey: [`contact-${contactId}`, 'notes'] });
            const prevNotes = queryClient.getQueryData([
                `contact-${contactId}`,
                'notes',
            ]) as ContactNote[];
            if (prevNotes === undefined) return;
            queryClient.setQueryData([`contact-${contactId}`, 'notes'], (prevData: any) => {
                return prevNotes
                    .filter(hasDistinctNoteId(noteId))
                    .sort(sortNotesByDate)
                    .sort(sortNotesByPinned);
            });
            return { prevNotes };
        },
        onError(error, variables, context) {
            queryClient.setQueryData([`contact-${contactId}`, 'notes'], context?.prevNotes);
        },
    });
}

export function useCreateNote(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateContactNoteRequest) => requestCreateContactNote(data),
        onMutate: async (note: CreateContactNoteRequest) => {
            await queryClient.cancelQueries({ queryKey: [`contact-${contactId}`, 'notes'] });
            const current = queryClient.getQueryData([`contact-${contactId}`, 'notes']);
            /* queryClient.setQueryData(['contactNotes', contactId], (prevData: any) => {
                //adding a temp id so contactNoteDisplay won't break with missing id while refetching the query for the new data
                //Need to have uuid implemented for this. But it is not needed for notes
                return [...prevData, {...note, noteId: -1, username: userName}].sort(sortNotesByDate).sort(sortNotesByPinned);
            }) */
            return { current };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData([`contact-${contactId}`, 'notes'], context?.current);
        },
        onSettled: (data, error, variables, context) => {
            queryClient.refetchQueries({
                queryKey: [`contact-${contactId}`, 'notes'],
            });
        },
    });
}

export function useEditNote(contactId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: UpdateContactNoteRequest) => requestEditContactNote(data),
        onMutate: async (data: UpdateContactNoteRequest) => {
            await queryClient.cancelQueries({ queryKey: [`contact-${contactId}`, 'notes'] });
            const prevNotes = queryClient.getQueryData([
                `contact-${contactId}`,
                'notes',
            ]) as ContactNote[];
            if (prevNotes === undefined) return;
            queryClient.setQueryData(
                [`contact-${contactId}`, 'notes'],
                prevNotes
                    .map(note => (note.noteId === data.noteId ? { ...note, ...data } : note))
                    .sort(sortNotesByDate)
                    .sort(sortNotesByPinned),
            );
            return { prevNotes };
        },
        onError(error, variables, context) {
            queryClient.refetchQueries({
                queryKey: [`contact-${contactId}`, 'notes'],
            });
        },
    });
}
